/**
 * Project slice actions — CRUD operations for projects and auto-save.
 *
 * Projects are persisted to MongoDB via the Node.js backend. Each project stores
 * its configuration (collections, mappings, filters, parameters, ratings) as a
 * single JSON document. The saveProject action is called automatically on state
 * changes to keep the backend in sync.
 */
import { backendNode, fetchAnalysisStatus } from "System/system";
import store from "System/reduxStore";
import { buildCalcParamDefaults } from "Config/calcParamsConfig";

// -------------------------------------------------------
// -------------------------------------------------------
// Persists the current project state to MongoDB. Shows a saving indicator
// for 3.5 seconds after completion (saving: true -> false -> null).
export const saveProject = () => async (dispatch) => {
  dispatch({ type: "SET_SAVING", saving: true });
  const data = store.getState();
  const project = JSON.parse(JSON.stringify(data.project));
  const { id } = project;
  project.modified = Date.now();
  const projectString = JSON.stringify(project);
  await backendNode("project", "save", { projectid: id, projectString });
  dispatch({ type: "SET_SAVING", saving: false });
  setTimeout(() => {
    dispatch({ type: "SET_SAVING", saving: null });
  }, 3500);
};

// Default analysis parameters for new projects — defines thresholds for supplier eligibility,
// invoice eligibility, performance ratios, and advance rate calculations.
const parameters_start = {
  referenceDate: "",
  linear: [
    {
      area: "Supplier Eligibility",
      entries: [
        {
          id: "min_days_trading_with_buyer",
          text: "Minimum Days Trading with Buyer",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 365,
          defaultValue: 365,
        },
        {
          id: "invoice_window_size",
          text: "Invoice Window Size [n]",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 90,
          defaultValue: 90,
        },
        {
          id: "min_number_of_invoices",
          text: "Minimum Number of Invoices within the last [n] days",
          description: "Description here",
          type: "integer",
          unit: "",
          value: 3,
          defaultValue: 3,
        },
        {
          id: "invoicing_period_window_size",
          text: "Invoicing Period Window Size [n]",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 365,
          defaultValue: 365,
        },
        {
          id: "min_invoicing_months_in_period",
          text: "Minimum Invoicing Months in Period [n]",
          description: "Description here",
          type: "integer",
          unit: "",
          value: 9,
          defaultValue: 9,
        },
        {
          id: "collections_in_period",
          text: "Minimum Collections in Period [n]",
          description: "Description here",
          type: "integer",
          unit: "",
          value: 10,
          defaultValue: 10,
        },
      ]
    },
    {
      area: "Invoice Eligibility",
      entries: [
        {
          id: "max_terms",
          text: "Max Terms",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 120,
          defaultValue: 120,
        },
        {
          id: "max_days_past_due",
          text: "Max Days Past Due",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 120,
          defaultValue: 120,
        },
        {
          id: "ineligible_currencies",
          text: "Ineligible Currencies",
          description: "Description here",
          type: "array",
          pool: [],
          value: [],
        },
        {
          id: "ineligible_document_types",
          text: "Ineligible Document Types",
          description: "Description here",
          type: "array",
          pool: [],
          value: [],
        },
      ],
    },
    {
      area: "Supplier Performance Ratios",
      entries: [
        {
          id: "dilution_sales_horizon",
          text: "Dilution/Sales Horizon",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 120,
          defaultValue: 120,
        },
        {
          id: "deemed_dilution_starting_point",
          text: "Deemed Dilution Starting Point",
          description: "Description here",
          type: "toggle",
          options: [{value: "issue-date", text: "Issue Date"}, {value: "due-date", text: "Due Date"}],
          value: "issue-date",
          defaultValue: "issue-date",
        },
        {
          id: "deemed_dilution_cutoff",
          text: "Deemed Dilution Cutoff",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 60,
          defaultValue: 60,
        },
        {
          id: "max_funding_tenor",
          text: "Max Funding Tenor",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 120,
          defaultValue: 120,
        },
        {
          id: "delinquency_cutoff",
          text: "Delinquency Cutoff",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 60,
          defaultValue: 60,
        },
        {
          id: "averaging_window",
          text: "Averaging Window",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 273,
          defaultValue: 273,
        },
        {
          id: "delinquency_threshold",
          text: "Delinquency Threshold",
          description: "Description here",
          type: "float",
          unit: "%",
          value: 5.0,
          defaultValue: 5.0,
        },
        {
          id: "dilution_threshold",
          text: "Dilution Threshold",
          description: "Description here",
          type: "float",
          unit: "%",
          value: 7.5,
          defaultValue: 7.5,
        },
        {
          id: "dso",
          text: "DSO",
          description: "Description here",
          type: "integer",
          unit: "Days",
          value: 90,
          defaultValue: 90,
        },
      ],
    },
    {
      area: "Advance Rate",
      entries: [
        {
          id: "stress",
          text: "Stress",
          description: "Description here",
          type: "float",
          value: 2,
          defaultValue: 2,
        },
        {
          id: "volatility_factor",
          text: "Volatility Factor",
          description: "Description here",
          type: "float",
          value: 1.96,
          defaultValue: 1.96,
        },
        {
          id: "max_advance_rate",
          text: "Max Advance Rate",
          description: "Description here",
          type: "integer",
          unit: "%",
          value: 93,
          defaultValue: 93,
        },
      ],
    },
  ],
};

// -------------------------------------------------------
// -------------------------------------------------------
// Loads a project from MongoDB and backfills missing fields with defaults.
// This ensures backward compatibility when new features are added — older projects
// that lack newer fields (e.g., calcParams, ratingScale) get sensible defaults.
// Also fetches analysis status flags from the Python backend to know which analyses exist.
export const loadProject = (projectid, cb) => async (dispatch) => {
  const result = await backendNode("project", "load", { projectid });
  let project = null;
  if (result.data) {
    project = result.data;
    delete project._id; // Strip MongoDB internal ID
    project.id = projectid;

    // Backfill defaults for fields that may not exist on older projects
    if (!project.filters) {
      project.filters = [];
    }

    if (!project.regexFilters) {
      project.regexFilters = [];
    }

    if(!project.parameters) {
      project.parameters = parameters_start;
    }

    if (!project.projectType) {
      project.projectType = "receivables";
    }

    if (!project.normMap) {
      project.normMap = {};
    }

    if (!project.ratingScale) {
      project.ratingScale = [
        { rating: "AA", concLimitPct: 100.0 },
        { rating: "A", concLimitPct: 17.28 },
        { rating: "BBB", concLimitPct: 8.64 },
        { rating: "NIG/NR", concLimitPct: 4.32 },
      ];
    }

    if (!project.buyerRatings) {
      project.buyerRatings = {};
    }

    if (!project.calcParams) {
      project.calcParams = buildCalcParamDefaults();
    }

    if(!project.invoiceParameters) {
      project.invoiceParameters = {
        nonCurrencies: [],
        nonDocumentTypes: [],
        approach: "due-date",
        termsA: 90,
        termsB: 90
    }}

    if (!project.scenarios) {
      project.scenarios = [];
    }

    dispatch({ type: "SET_PROJECT", project });

    // fetch analysis status flags from Python backend
    try {
      const status = await fetchAnalysisStatus(projectid);
      dispatch({ type: "SET_ANALYSIS_STATUS", status });
    } catch (e) {
      console.log("Could not fetch analysis status", e);
    }
  }
  cb(project);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Creates a new project with empty data structures and persists it to MongoDB.
// Returns the new project ID via callback.
export const newProject = (title, projectType, cb) => async (dispatch) => {
  const { uid } = store.getState().user;
  const project = {
    title,
    projectType,
    dirs: [],
    collections: [],
    repository: {},
    flatFiles: [],
    included: [],
    setup: Date.now(),
    modified: Date.now(),
    uid,
  };

  const response = await backendNode("project", "setup", {
    projectString: JSON.stringify(project),
  });

  let projectid = null;
  if (response.data) {
    projectid = response.data.projectid;
    project.id = projectid;
    dispatch({ type: "SET_PROJECT", project });
  }

  cb(projectid);
};

// -------------------------------------------------------
// -------------------------------------------------------
// Deletes a project from the backend and removes it from the Redux projects list
export const eraseProject = (projectid) => async (dispatch) => {
  await backendNode("project", "erase", { projectid });
  dispatch({ type: "ERASE_PROJECT", projectid });
};

// -------------------------------------------------------
// -------------------------------------------------------
// Toggles the buyer-perspective view mode (affects how numeric signs are displayed in tables)
export const setBuyerView = (buyerView) => async (dispatch) => {
  dispatch({ type: "SET_BUYER_VIEW", buyerView });
};

// -------------------------------------------------------
// Template management — save/load reusable configuration templates
// -------------------------------------------------------

/** List available templates of a given type for the current user. */
export const listTemplates = (templateType, cb) => async () => {
  const { uid } = store.getState().user;
  const result = await backendNode("templates", "list", { uid, templateType });
  cb(result.data?.templates || []);
};

/** Save the current configuration as a named template. */
export const saveTemplate = (name, templateType, data, cb) => async () => {
  const { uid } = store.getState().user;
  await backendNode("templates", "save", {
    uid,
    name,
    templateType,
    dataString: JSON.stringify(data),
  });
  if (cb) cb();
};

/** Load a template by ID and apply it to the current project. */
export const loadTemplate = (templateid, templateType, cb) => async (dispatch) => {
  try {
    const result = await backendNode("templates", "load", { templateid });
    if (result.data && result.data.data) {
      const data = result.data.data;
      switch (templateType) {
        case "normMap":
          dispatch({ type: "SET_NORM_MAP", normMap: data });
          break;
        case "buyerRatings":
          dispatch({ type: "SET_BUYER_RATINGS", buyerRatings: data });
          break;
        case "ratingScale":
          dispatch({ type: "SET_RATING_SCALE", ratingScale: data });
          break;
        case "calcParams":
          Object.entries(data).forEach(([section, params]) => {
            dispatch({ type: "SET_CALC_PARAMS", section, params });
          });
          break;
        default:
          break;
      }
      if (cb) cb(data);
      dispatch(saveProject());
    } else {
      console.error("Template load: no data in response", result.data);
      if (cb) cb(null);
    }
  } catch (err) {
    console.error("Template load error:", err);
    if (cb) cb(null);
  }
};

/** Delete a template by ID. */
export const deleteTemplate = (templateid, cb) => async () => {
  await backendNode("templates", "erase", { templateid });
  if (cb) cb();
};
